import React, { useState } from 'react';
import {
  Smartphone,
  Monitor,
  Download,
  ShieldCheck,
  Zap,
  Lock,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Copy,
  ChevronDown,
  Layers,
  Globe,
  Radio,
  ExternalLink,
  Cpu,
  KeyRound,
  ShieldAlert,
  Sliders,
  Check,
  BookOpen,
  X as CloseIcon
} from 'lucide-react';
import { LiveDemoSimulator } from './LiveDemoSimulator';
import { CkLogo } from './CkLogo';

interface LandingPageProps {
  onOpenDownloads: () => void;
  onOpenGuide: (tab?: 'android' | 'windows' | 'security') => void;
  onLaunchWebApp: () => void;
  onOpenScanner?: () => void;
  hasActiveRoom?: boolean;
  onViewLiveConsole?: () => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({
  onOpenDownloads,
  onOpenGuide,
  onLaunchWebApp,
  onOpenScanner,
  hasActiveRoom,
  onViewLiveConsole
}) => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [guideTab, setGuideTab] = useState<'android' | 'windows'>('android');

  const faqs = [
    {
      q: 'Do I need to keep the CK Share app open on my Android phone?',
      a: 'No! CK Share features a persistent background foreground service and an Accessibility copy listener. Once you join your room, you can close or swipe away the app. When you copy text in Chrome, WhatsApp, or Notes, it automatically broadcasts to your PC.'
    },
    {
      q: 'Will the background sync drain my phone battery?',
      a: 'No. CK Share does not use battery-heavy polling loops. It is 100% event-driven, meaning the daemon sleeps until an OS clipboard copy event occurs or a push is received over WebSocket, using virtually 0% battery.'
    },
    {
      q: 'Can anyone on the Cloudflare relay read my passwords or copied text?',
      a: 'Mathematically impossible. All clipboard content is encrypted client-side using AES-GCM-256 with keys derived from your password via PBKDF2 (100,000 rounds). The Cloudflare relay only routes opaque ciphertext and has zero plaintext visibility.'
    },
    {
      q: 'Do I have to install software on my Windows PC?',
      a: 'You can use the Web App on any browser with zero installation! However, if you want zero-click automatic sync (press Ctrl+C anywhere in Windows to broadcast), you can download our lightweight 37 KB CKShareTray.exe companion.'
    },
    {
      q: 'Why does Android ask for Accessibility Service permission?',
      a: 'Android 10+ restricts background apps from reading clipboard data. The Accessibility Service allows CK Share to safely detect when you tap "Copy" in other apps to broadcast the text to your PC immediately.'
    },
    {
      q: 'Is CK Share free and open source?',
      a: 'Yes, CK Share is 100% free, open source, and has zero ads, zero telemetry, and zero subscription fees.'
    }
  ];

  return (
    <div className="w-full text-gray-200 overflow-hidden">
      
      {/* 1. HERO SECTION */}
      <section className="relative pt-12 pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto flex flex-col items-center text-center">
        
        {/* Glow Halo */}
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-96 h-96 bg-gradient-to-tr from-cyan-500/20 via-purple-600/20 to-transparent rounded-full blur-3xl pointer-events-none -z-10" />

        {/* Hero Logo Emblem */}
        <div className="relative mb-6">
          <CkLogo size={76} className="hover:scale-105 transition-transform drop-shadow-[0_0_35px_rgba(0,240,255,0.5)] cursor-pointer" />
          <div className="absolute -inset-2 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-full blur-xl -z-10 animate-pulse pointer-events-none" />
        </div>

        {/* Top Badges */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/30 text-cyan-300 text-xs font-semibold shadow-[0_0_20px_rgba(0,240,255,0.15)] mb-6 animate-fadeIn">
          <span className="flex h-2 w-2 relative">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
          </span>
          <span>Zero-Click Universal Clipboard Sync for Android &amp; Windows</span>
        </div>

        {/* Main Headline */}
        <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold text-white tracking-tight font-display max-w-4xl leading-[1.1] mb-6">
          Copy on Phone.{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-teal-300 to-purple-400">
            Paste on PC.
          </span>{' '}
          Instantly.
        </h1>

        {/* Subtitle */}
        <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
          Hardware-accelerated <strong className="text-gray-200">AES-256 encrypted</strong> zero-click clipboard synchronization. No accounts. No subscriptions. No cloud data storage. Powered by Cloudflare Edge.
        </p>

        {/* Primary CTA Button Group */}
        <div className="flex items-center justify-center flex-wrap gap-4 mb-12">
          
          <button
            onClick={onOpenDownloads}
            className="flex items-center gap-2 px-6 py-3.5 rounded-2xl text-sm font-bold text-black bg-gradient-to-r from-cyan-400 via-teal-300 to-emerald-400 hover:from-cyan-300 hover:to-emerald-300 shadow-[0_0_30px_rgba(0,240,255,0.35)] hover:shadow-[0_0_40px_rgba(0,240,255,0.55)] transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            <Download size={18} />
            <span>Download for Android &amp; Windows</span>
          </button>

          {hasActiveRoom && onViewLiveConsole ? (
            <button
              onClick={onViewLiveConsole}
              className="flex items-center gap-2 px-6 py-3.5 rounded-2xl text-sm font-bold text-white bg-white/10 hover:bg-white/15 border border-white/20 hover:border-cyan-400/50 shadow-lg backdrop-blur-xl transition-all"
            >
              <Sparkles size={18} className="text-cyan-400" />
              <span>Open Live Sync Console</span>
            </button>
          ) : (
            <button
              onClick={onLaunchWebApp}
              className="flex items-center gap-2 px-6 py-3.5 rounded-2xl text-sm font-bold text-white bg-white/10 hover:bg-white/15 border border-white/20 hover:border-cyan-400/50 shadow-lg backdrop-blur-xl transition-all"
            >
              <Sparkles size={18} className="text-cyan-400" />
              <span>Launch Web Console</span>
            </button>
          )}

          {onOpenScanner && (
            <button
              onClick={onOpenScanner}
              className="flex items-center gap-2 px-4 py-3.5 rounded-2xl text-xs font-semibold text-gray-300 hover:text-white bg-black/40 hover:bg-white/5 border border-white/10 transition-all"
            >
              <span>📷 Scan QR Code</span>
            </button>
          )}
        </div>

        {/* Feature Highlights Trust Badges */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto mb-14 text-xs text-gray-400">
          <div className="flex items-center justify-center gap-2 p-2.5 rounded-xl bg-white/[0.02] border border-white/5">
            <Zap size={14} className="text-cyan-400" />
            <span>0.0s Zero-Click Sync</span>
          </div>
          <div className="flex items-center justify-center gap-2 p-2.5 rounded-xl bg-white/[0.02] border border-white/5">
            <Lock size={14} className="text-purple-400" />
            <span>AES-GCM-256 E2EE</span>
          </div>
          <div className="flex items-center justify-center gap-2 p-2.5 rounded-xl bg-white/[0.02] border border-white/5">
            <Radio size={14} className="text-emerald-400" />
            <span>&lt; 15ms Cloudflare Relay</span>
          </div>
          <div className="flex items-center justify-center gap-2 p-2.5 rounded-xl bg-white/[0.02] border border-white/5">
            <ShieldCheck size={14} className="text-blue-400" />
            <span>100% Free &amp; Open Source</span>
          </div>
        </div>

        {/* Interactive Hero Simulator */}
        <div className="w-full">
          <LiveDemoSimulator />
        </div>

      </section>

      {/* 2. FEATURES GRID SECTION */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto border-t border-white/5">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-xs font-bold text-cyan-400 uppercase tracking-widest mb-2">Engineered for Perfection</h2>
          <p className="text-3xl sm:text-4xl font-extrabold text-white font-display">
            Built for developers, power users, and privacy purists
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          
          {/* Card 1 */}
          <div className="p-6 rounded-2xl bg-gradient-to-b from-white/[0.03] to-transparent border border-white/10 hover:border-cyan-500/40 transition-all group">
            <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400 mb-5 group-hover:scale-110 transition-transform">
              <Zap size={24} />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">Zero-Click Auto Broadcast</h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              Copy any text, token, or link in Chrome, WhatsApp, or VS Code. It automatically synchronizes to your other devices without opening any apps.
            </p>
          </div>

          {/* Card 2 */}
          <div className="p-6 rounded-2xl bg-gradient-to-b from-white/[0.03] to-transparent border border-white/10 hover:border-purple-500/40 transition-all group">
            <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center text-purple-400 mb-5 group-hover:scale-110 transition-transform">
              <Lock size={24} />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">Zero-Knowledge E2EE</h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              Everything is encrypted client-side using authenticated AES-GCM-256 and PBKDF2 (100,000 iterations). Plaintext never touches any server.
            </p>
          </div>

          {/* Card 3 */}
          <div className="p-6 rounded-2xl bg-gradient-to-b from-white/[0.03] to-transparent border border-white/10 hover:border-emerald-500/40 transition-all group">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 mb-5 group-hover:scale-110 transition-transform">
              <Radio size={24} />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">Cloudflare Edge Relay</h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              State-of-the-art Durable Objects and WebSockets routed across 275+ Cloudflare edge cities worldwide for sub-15ms lightning transfer speeds.
            </p>
          </div>

          {/* Card 4 */}
          <div className="p-6 rounded-2xl bg-gradient-to-b from-white/[0.03] to-transparent border border-white/10 hover:border-indigo-500/40 transition-all group">
            <div className="w-12 h-12 rounded-xl bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center text-indigo-400 mb-5 group-hover:scale-110 transition-transform">
              <Monitor size={24} />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">37 KB Windows Companion</h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              Ultra-lightweight native C# companion that sits silently in your Windows Tray with 0% CPU consumption and native Win32 clipboard hooks.
            </p>
          </div>

          {/* Card 5 */}
          <div className="p-6 rounded-2xl bg-gradient-to-b from-white/[0.03] to-transparent border border-white/10 hover:border-teal-500/40 transition-all group">
            <div className="w-12 h-12 rounded-xl bg-teal-500/10 border border-teal-500/30 flex items-center justify-center text-teal-400 mb-5 group-hover:scale-110 transition-transform">
              <Smartphone size={24} />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">1-Tap Android Notification Sync</h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              Includes a dedicated <span className="text-cyan-300 font-mono">⚡ "Sync Clipboard Now"</span> button on your Android notification tray for instantaneous 1-tap manual sync.
            </p>
          </div>

          {/* Card 6 */}
          <div className="p-6 rounded-2xl bg-gradient-to-b from-white/[0.03] to-transparent border border-white/10 hover:border-pink-500/40 transition-all group">
            <div className="w-12 h-12 rounded-xl bg-pink-500/10 border border-pink-500/30 flex items-center justify-center text-pink-400 mb-5 group-hover:scale-110 transition-transform">
              <Layers size={24} />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">Quick Drop Dropzone</h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              Drag &amp; drop files, images, code snippets, or markdown documents into the Web App dropzone for instant encrypted P2P broadcast.
            </p>
          </div>

        </div>
      </section>

      {/* 3. HOW IT WORKS SECTION */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto border-t border-white/5">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-xs font-bold text-cyan-400 uppercase tracking-widest mb-2">Effortless 3-Step Setup</h2>
          <p className="text-3xl sm:text-4xl font-extrabold text-white font-display">
            How CK Share Works in 10 Seconds
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 relative flex flex-col justify-between">
            <div>
              <div className="w-10 h-10 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 flex items-center justify-center text-sm font-bold mb-4 font-mono">
                01
              </div>
              <h3 className="text-base font-bold text-white mb-2">Create Your Room</h3>
              <p className="text-xs text-gray-400 leading-relaxed">
                Click "Launch Web App" or open the Android/Windows app to generate a private 4-digit Room Code (e.g. <strong className="text-cyan-300">CK-9872</strong>) and cryptographic key.
              </p>
            </div>
            <div className="mt-6 p-3 rounded-xl bg-black/40 border border-white/5 font-mono text-[11px] text-cyan-300">
              Room: CK-9872 • AES-256 Key Ready
            </div>
          </div>

          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 relative flex flex-col justify-between">
            <div>
              <div className="w-10 h-10 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/40 flex items-center justify-center text-sm font-bold mb-4 font-mono">
                02
              </div>
              <h3 className="text-base font-bold text-white mb-2">Pair Your Devices</h3>
              <p className="text-xs text-gray-400 leading-relaxed">
                Scan the on-screen QR code from your phone's camera or enter your Room Code into the Windows Tray settings. Both devices connect to the same encrypted mesh.
              </p>
            </div>
            <div className="mt-6 p-3 rounded-xl bg-black/40 border border-white/5 font-mono text-[11px] text-purple-300">
              Mesh: Phone (Android) ⇄ PC (Windows)
            </div>
          </div>

          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 relative flex flex-col justify-between">
            <div>
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 flex items-center justify-center text-sm font-bold mb-4 font-mono">
                03
              </div>
              <h3 className="text-base font-bold text-white mb-2">Copy &amp; Paste Seamlessly</h3>
              <p className="text-xs text-gray-400 leading-relaxed">
                Copy text anywhere on your phone ➔ Press <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-white font-bold">Ctrl + V</kbd> on PC! Copy on PC (<kbd className="px-1.5 py-0.5 bg-white/10 rounded text-white font-bold">Ctrl + C</kbd>) ➔ Paste on phone!
              </p>
            </div>
            <div className="mt-6 p-3 rounded-xl bg-black/40 border border-white/5 font-mono text-[11px] text-emerald-300">
              ⚡ Instant Sync Verified &lt; 15ms
            </div>
          </div>

        </div>
      </section>

      {/* 4. DOWNLOADS SECTION */}
      <section id="downloads" className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto border-t border-white/5">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-xs font-bold text-cyan-400 uppercase tracking-widest mb-2">Direct Downloads</h2>
          <p className="text-3xl sm:text-4xl font-extrabold text-white font-display">
            Get CK Share for Your Devices
          </p>
          <p className="text-xs text-gray-400 mt-2">Zero spyware, zero tracking, direct standalone binaries</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          
          {/* Android Download Card */}
          <div className="p-6 rounded-3xl bg-gradient-to-b from-white/[0.04] to-black/60 border border-white/10 hover:border-emerald-500/40 transition-all flex flex-col justify-between">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                  <Smartphone size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Android Phone App</h3>
                  <p className="text-xs text-gray-400">Android 8.0 through Android 15+</p>
                </div>
              </div>

              <p className="text-xs text-gray-300 leading-relaxed">
                Native APK with persistent background foreground service, Accessibility copy listener, and 1-tap notification quick-action button.
              </p>

              <div className="space-y-2 text-xs text-gray-400">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-emerald-400" />
                  <span>Zero-click auto copy in WhatsApp &amp; Chrome</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-emerald-400" />
                  <span>⚡ Notification quick-sync button</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-emerald-400" />
                  <span>0% Battery drain (event-driven)</span>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-4 border-t border-white/10 flex items-center justify-between gap-3">
              <span className="text-xs text-gray-400 font-mono">Size: <strong>~5.3 MB</strong></span>
              <a
                href="/downloads/ck-share-android.apk"
                download="ck-share-android.apk"
                className="flex items-center gap-2 px-5 py-2.5 text-xs font-bold text-black bg-gradient-to-r from-cyan-400 to-emerald-400 hover:from-cyan-300 hover:to-emerald-300 rounded-xl shadow-[0_0_20px_rgba(0,240,255,0.3)] transition-all"
              >
                <Download size={15} />
                Download APK
              </a>
            </div>
          </div>

          {/* Windows Download Card */}
          <div className="p-6 rounded-3xl bg-gradient-to-b from-white/[0.04] to-black/60 border border-white/10 hover:border-purple-500/40 transition-all flex flex-col justify-between">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center text-purple-400">
                  <Monitor size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Windows Desktop Companion</h3>
                  <p className="text-xs text-gray-400">Windows 10 &amp; Windows 11 (64-bit)</p>
                </div>
              </div>

              <p className="text-xs text-gray-300 leading-relaxed">
                Lightweight native C# System Tray companion. Intercepts global copy events via Win32 hooks and syncs with hardware AES-GCM-256.
              </p>

              <div className="space-y-2 text-xs text-gray-400">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-purple-400" />
                  <span>Copies from VS Code, Chrome, Terminal, Office</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-purple-400" />
                  <span>Silent PC-to-Phone copy (no self-echo)</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-purple-400" />
                  <span>Zero dependencies (Standalone 37 KB)</span>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-4 border-t border-white/10 flex items-center justify-between gap-3">
              <span className="text-xs text-gray-400 font-mono">Size: <strong>37 KB</strong></span>
              <a
                href="/downloads/CKShareTray.exe"
                download="CKShareTray.exe"
                className="flex items-center gap-2 px-5 py-2.5 text-xs font-bold text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 rounded-xl shadow-[0_0_20px_rgba(168,85,247,0.3)] transition-all"
              >
                <Download size={15} />
                Download EXE
              </a>
            </div>
          </div>

        </div>
      </section>

      {/* 5. STEP-BY-STEP GUIDE SECTION */}
      <section id="guide" className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto border-t border-white/5">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-xs font-bold text-cyan-400 uppercase tracking-widest mb-2">Clear &amp; Simple Guides</h2>
          <p className="text-3xl sm:text-4xl font-extrabold text-white font-display">
            Step-by-Step Setup Guide
          </p>
          <p className="text-xs text-gray-400 mt-2">Get universal zero-click clipboard sync running in under 60 seconds</p>
        </div>

        {/* Interactive Guide Tab Switcher */}
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-8">
            <button
              onClick={() => setGuideTab('android')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all ${
                guideTab === 'android'
                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 shadow-[0_0_20px_rgba(16,185,129,0.2)]'
                  : 'bg-white/5 text-gray-400 hover:text-white border border-white/5 hover:bg-white/10'
              }`}
            >
              <Smartphone size={16} className="text-emerald-400" />
              <span>Android Phone Setup</span>
            </button>

            <button
              onClick={() => setGuideTab('windows')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all ${
                guideTab === 'windows'
                  ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40 shadow-[0_0_20px_rgba(168,85,247,0.2)]'
                  : 'bg-white/5 text-gray-400 hover:text-white border border-white/5 hover:bg-white/10'
              }`}
            >
              <Monitor size={16} className="text-purple-400" />
              <span>Windows PC Companion</span>
            </button>
          </div>

          {/* Android Guide Content */}
          {guideTab === 'android' && (
            <div className="space-y-4 p-6 sm:p-8 rounded-3xl bg-gradient-to-b from-white/[0.03] to-black/50 border border-emerald-500/30 animate-fadeIn">
              <div className="flex items-center justify-between flex-wrap gap-4 pb-4 border-b border-white/10">
                <div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <Smartphone size={18} className="text-emerald-400" />
                    <span>Android Phone Quick Walkthrough</span>
                  </h3>
                  <p className="text-xs text-gray-400">Zero-click background sync across all your mobile apps</p>
                </div>
                <button
                  onClick={() => onOpenGuide('android')}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded-xl transition-all"
                >
                  <BookOpen size={14} />
                  <span>Open Full Interactive Modal</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5">
                  <div className="w-7 h-7 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold text-xs mb-3 font-mono">1</div>
                  <h4 className="text-sm font-semibold text-white mb-1">Install APK</h4>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Download <strong className="text-gray-200">ck-share-android.apk</strong> and enable "Allow from this source" in Android settings.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5">
                  <div className="w-7 h-7 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold text-xs mb-3 font-mono">2</div>
                  <h4 className="text-sm font-semibold text-white mb-1">Enable Accessibility</h4>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Turn on <strong className="text-gray-200">CK Share Service</strong> in Accessibility to enable background copy detection on Android 10+.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5">
                  <div className="w-7 h-7 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold text-xs mb-3 font-mono">3</div>
                  <h4 className="text-sm font-semibold text-white mb-1">Copy &amp; Paste!</h4>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Copy text in WhatsApp or Chrome ➔ It instantly syncs to PC. Or use the ⚡ notification quick action!
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Windows Guide Content */}
          {guideTab === 'windows' && (
            <div className="space-y-4 p-6 sm:p-8 rounded-3xl bg-gradient-to-b from-white/[0.03] to-black/50 border border-purple-500/30 animate-fadeIn">
              <div className="flex items-center justify-between flex-wrap gap-4 pb-4 border-b border-white/10">
                <div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <Monitor size={18} className="text-purple-400" />
                    <span>Windows PC Companion Walkthrough</span>
                  </h3>
                  <p className="text-xs text-gray-400">Silent 37 KB Tray companion with zero dependencies</p>
                </div>
                <button
                  onClick={() => onOpenGuide('windows')}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-purple-300 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 rounded-xl transition-all"
                >
                  <BookOpen size={14} />
                  <span>Open Full Interactive Modal</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5">
                  <div className="w-7 h-7 rounded-lg bg-purple-500/20 text-purple-400 flex items-center justify-center font-bold text-xs mb-3 font-mono">1</div>
                  <h4 className="text-sm font-semibold text-white mb-1">Launch Tray App</h4>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Download and run <strong className="text-gray-200">CKShareTray.exe</strong>. It sits quietly in your taskbar tray.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5">
                  <div className="w-7 h-7 rounded-lg bg-purple-500/20 text-purple-400 flex items-center justify-center font-bold text-xs mb-3 font-mono">2</div>
                  <h4 className="text-sm font-semibold text-white mb-1">Pair Room Code</h4>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Right-click tray icon ➔ Settings ➔ Enter your Room Code &amp; Key. Connects in milliseconds.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5">
                  <div className="w-7 h-7 rounded-lg bg-purple-500/20 text-purple-400 flex items-center justify-center font-bold text-xs mb-3 font-mono">3</div>
                  <h4 className="text-sm font-semibold text-white mb-1">Ctrl+C ➔ Phone!</h4>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Press <kbd className="px-1 py-0.5 bg-white/10 rounded text-[11px] text-white">Ctrl+C</kbd> in VS Code or Chrome ➔ Silently arrives on your phone immediately!
                  </p>
                </div>
              </div>
            </div>
          )}

        </div>
      </section>

      {/* 6. SECURITY & E2EE SECTION */}
      <section id="security" className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto border-t border-white/5">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-xs font-bold text-cyan-400 uppercase tracking-widest mb-2">Zero-Knowledge Architecture</h2>
          <p className="text-3xl sm:text-4xl font-extrabold text-white font-display">
            End-to-End Encryption &amp; Security
          </p>
          <p className="text-xs text-gray-400 mt-2">Hardware-accelerated client-side cryptography. Zero plaintext ever touches any server.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          
          <div className="p-6 rounded-2xl bg-gradient-to-b from-white/[0.03] to-transparent border border-white/10 hover:border-cyan-500/40 transition-all">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400 mb-4">
              <Lock size={20} />
            </div>
            <h3 className="text-sm font-bold text-white mb-1.5">Client-Side AES-GCM-256</h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              Every clipboard item is encrypted locally before dispatch using AES-256 Galois/Counter Mode with 12-byte random IVs.
            </p>
          </div>

          <div className="p-6 rounded-2xl bg-gradient-to-b from-white/[0.03] to-transparent border border-white/10 hover:border-purple-500/40 transition-all">
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center text-purple-400 mb-4">
              <KeyRound size={20} />
            </div>
            <h3 className="text-sm font-bold text-white mb-1.5">PBKDF2 Key Derivation</h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              Keys derived using HMAC-SHA256 with 100,000 rounds and unique salt per room. Passwords never travel across the wire.
            </p>
          </div>

          <div className="p-6 rounded-2xl bg-gradient-to-b from-white/[0.03] to-transparent border border-white/10 hover:border-emerald-500/40 transition-all">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 mb-4">
              <Radio size={20} />
            </div>
            <h3 className="text-sm font-bold text-white mb-1.5">Stateless Blind Relay</h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              Cloudflare edge nodes only see opaque binary ciphertext. The server has mathematically zero decryption capabilities.
            </p>
          </div>

          <div className="p-6 rounded-2xl bg-gradient-to-b from-white/[0.03] to-transparent border border-white/10 hover:border-blue-500/40 transition-all">
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-400 mb-4">
              <ShieldCheck size={20} />
            </div>
            <h3 className="text-sm font-bold text-white mb-1.5">Zero Persistent Storage</h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              Messages are routed strictly in-memory and discarded upon delivery. No logs, no telemetry, and no disk databases.
            </p>
          </div>

        </div>

        {/* Security Whitepaper Strip */}
        <div className="p-6 rounded-2xl bg-gradient-to-r from-cyan-950/30 via-purple-950/20 to-black/40 border border-cyan-500/30 flex flex-col sm:flex-row items-center justify-between gap-4 max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <ShieldCheck size={28} className="text-cyan-400 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-bold text-white">Full Cryptographic Whitepaper &amp; Policy Disclosures</h4>
              <p className="text-xs text-gray-400">Review Google Play Accessibility API compliance, threat model, and cryptographic specs.</p>
            </div>
          </div>
          <button
            onClick={() => onOpenGuide('security')}
            className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-xs font-bold text-black bg-cyan-400 hover:bg-cyan-300 shadow-[0_0_20px_rgba(0,240,255,0.3)] transition-all whitespace-nowrap"
          >
            <ShieldCheck size={14} />
            <span>Open Security Whitepaper</span>
          </button>
        </div>
      </section>

      {/* 7. COMPARISON SECTION */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto border-t border-white/5">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-xs font-bold text-cyan-400 uppercase tracking-widest mb-2">Why CK Share Wins</h2>
          <p className="text-3xl sm:text-4xl font-extrabold text-white font-display">
            Comparison with Alternatives
          </p>
        </div>

        <div className="max-w-4xl mx-auto overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-white/10 text-gray-400">
                <th className="py-3 px-4 font-bold">Feature</th>
                <th className="py-3 px-4 text-cyan-300 font-bold bg-cyan-950/20 rounded-t-xl">CK Share</th>
                <th className="py-3 px-4">Apple Universal Clipboard</th>
                <th className="py-3 px-4">KDE Connect</th>
                <th className="py-3 px-4">Pushbullet</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5 text-gray-300">
              <tr>
                <td className="py-3 px-4 font-semibold text-white">Cross-Platform (Android + Windows)</td>
                <td className="py-3 px-4 text-emerald-400 font-bold bg-cyan-950/20">✅ Yes</td>
                <td className="py-3 px-4 text-rose-400 font-medium">❌ Apple Only</td>
                <td className="py-3 px-4 text-emerald-400 font-medium">✅ Yes</td>
                <td className="py-3 px-4 text-emerald-400 font-medium">✅ Yes</td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-semibold text-white">Zero-Click Background Sync</td>
                <td className="py-3 px-4 text-emerald-400 font-bold bg-cyan-950/20">✅ Yes</td>
                <td className="py-3 px-4 text-emerald-400 font-medium">✅ Yes</td>
                <td className="py-3 px-4 text-amber-400 font-medium">⚠️ Complex setup</td>
                <td className="py-3 px-4 text-rose-400 font-medium">❌ Manual / Paid</td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-semibold text-white">Client-Side AES-256 E2EE</td>
                <td className="py-3 px-4 text-emerald-400 font-bold bg-cyan-950/20">✅ 100% Zero-Knowledge</td>
                <td className="py-3 px-4 text-emerald-400 font-medium">✅ Yes</td>
                <td className="py-3 px-4 text-emerald-400 font-medium">✅ TLS</td>
                <td className="py-3 px-4 text-rose-400 font-medium">❌ Cloud Plaintext</td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-semibold text-white">No Account / Login Required</td>
                <td className="py-3 px-4 text-emerald-400 font-bold bg-cyan-950/20">✅ 4-Digit Room Code</td>
                <td className="py-3 px-4 text-rose-400 font-medium">❌ Apple ID Required</td>
                <td className="py-3 px-4 text-emerald-400 font-medium">✅ LAN pairing</td>
                <td className="py-3 px-4 text-rose-400 font-medium">❌ Google/Email Login</td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-semibold text-white">Works Over Internet (Different Wi-Fi)</td>
                <td className="py-3 px-4 text-emerald-400 font-bold bg-cyan-950/20">✅ Global Cloudflare Relay</td>
                <td className="py-3 px-4 text-rose-400 font-medium">❌ Local Bluetooth/Wi-Fi only</td>
                <td className="py-3 px-4 text-rose-400 font-medium">❌ Same LAN only</td>
                <td className="py-3 px-4 text-emerald-400 font-medium">✅ Yes</td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-semibold text-white">Price / Subscriptions</td>
                <td className="py-3 px-4 text-emerald-400 font-bold bg-cyan-950/20">✅ 100% Free Forever</td>
                <td className="py-3 px-4 text-gray-400 font-medium">Hardware lock-in</td>
                <td className="py-3 px-4 text-emerald-400 font-medium">✅ Free</td>
                <td className="py-3 px-4 text-rose-400 font-medium">❌ $4.99 / month Pro</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* 8. INTERACTIVE FAQ SECTION */}
      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto border-t border-white/5">
        <div className="text-center mb-16">
          <h2 className="text-xs font-bold text-cyan-400 uppercase tracking-widest mb-2">Got Questions?</h2>
          <p className="text-3xl sm:text-4xl font-extrabold text-white font-display">
            Frequently Asked Questions
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, index) => {
            const isOpen = openFaq === index;
            return (
              <div
                key={index}
                className="p-4 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-white/20 transition-all"
              >
                <button
                  onClick={() => setOpenFaq(isOpen ? null : index)}
                  className="w-full flex items-center justify-between text-left text-sm font-bold text-white gap-4"
                >
                  <span>{faq.q}</span>
                  <ChevronDown
                    size={16}
                    className={`text-cyan-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {isOpen && (
                  <p className="text-xs text-gray-400 leading-relaxed mt-3 pt-3 border-t border-white/5 animate-fadeIn">
                    {faq.a}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* 7. FINAL CTA BANNER */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <div className="p-8 sm:p-12 rounded-3xl bg-gradient-to-r from-cyan-950/60 via-purple-950/40 to-black/80 border border-cyan-500/40 shadow-[0_0_60px_rgba(0,240,255,0.2)] text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-80 h-80 bg-cyan-500/20 rounded-full blur-3xl pointer-events-none" />
          
          <h2 className="text-3xl sm:text-5xl font-extrabold text-white font-display mb-4">
            Experience Universal Sync Now
          </h2>
          <p className="text-xs sm:text-sm text-gray-300 max-w-xl mx-auto mb-8">
            Create a private room in 5 seconds. No credit card, no sign-up, zero data stored on servers.
          </p>

          <div className="flex items-center justify-center flex-wrap gap-4">
            <button
              onClick={onLaunchWebApp}
              className="px-8 py-3.5 rounded-2xl text-sm font-bold text-black bg-cyan-400 hover:bg-cyan-300 shadow-[0_0_30px_rgba(0,240,255,0.4)] transition-all hover:scale-105"
            >
              <span>Connect to Web Console</span>
            </button>
            <button
              onClick={onOpenDownloads}
              className="px-8 py-3.5 rounded-2xl text-sm font-bold text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded-2xl transition-all"
            >
              <span>Download Standalone Apps</span>
            </button>
          </div>
        </div>
      </section>

    </div>
  );
};
